import React, { useState, useEffect } from "react";
import {
  IconButton,
  Button,
  Fade,
  Paper,
  CircularProgress,
} from "@material-ui/core";
import {
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { getTokens, TOPBAR } from "../../styles/tokens";

const packageVersion = require("../../../package.json").version;

// Aviso no padrão dos toasts do painel: superfície do tema, filete e sombra elevada.
const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    banner: {
      position: "fixed",
      top: TOPBAR.height + 12,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: theme.zIndex.drawer + 2,
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "calc(100% - 24px)",
      maxWidth: 600,
      padding: "10px 12px 10px 14px",
      boxSizing: "border-box",
      backgroundColor: t.paper,
      color: t.text,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.lift,
      [theme.breakpoints.down("sm")]: {
        top: TOPBAR.compact + 10,
        flexWrap: "wrap",
      },
    },
    icon: {
      flex: "none",
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: t.radius.sm,
      backgroundColor: t.tint.soft,
      color: t.brand,
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    text: {
      flex: "1 1 220px",
      minWidth: 0,
      margin: 0,
      fontFamily: t.font,
      fontSize: 13.5,
      lineHeight: 1.45,
      color: t.textSecondary,
      "& strong": { fontWeight: 600, color: t.text },
    },
    actions: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginLeft: "auto",
    },
    close: { padding: 7, color: t.textSecondary },
  };
});

const VersionControl = () => {
  const classes = useStyles();
  const [storedVersion] = useState(
    window.localStorage.getItem("version") || packageVersion
  );
  const [showBanner, setShowBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const hasNewVersion = storedVersion !== packageVersion;

  useEffect(() => {
    if (hasNewVersion) {
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [hasNewVersion]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    window.localStorage.setItem("version", packageVersion);

    // Notifica backend (best-effort, não bloqueia o reload)
    try {
      await api.post("/version", { version: packageVersion });
    } catch (error) {
      console.error("Erro ao notificar versão:", error);
    }

    // Limpa cache e recarrega sempre
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
    } catch (_) {}

    window.location.reload();
  };

  if (!hasNewVersion) {
    return null;
  }

  return (
    <>
      {/* Banner de Atualização Centralizado */}
      <Fade in={showBanner && !isUpdating}>
        <Paper className={classes.banner} elevation={0} role="status">
          <div className={classes.icon} aria-hidden="true">
            <InfoIcon />
          </div>
          <p className={classes.text}>
            <strong>Nova versão {packageVersion} disponível!</strong>
            {" Clique em atualizar para obter as últimas melhorias."}
          </p>
          <div className={classes.actions}>
            {isUpdating && <CircularProgress size={20} />}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleUpdate}
              disabled={isUpdating}
              startIcon={!isUpdating && <RefreshIcon />}
            >
              {isUpdating ? "Atualizando..." : "Atualizar"}
            </Button>
            {!isUpdating && (
              <IconButton
                size="small"
                className={classes.close}
                onClick={() => setShowBanner(false)}
                aria-label="Fechar aviso"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </Paper>
      </Fade>
    </>
  );
};

export default VersionControl;
